import React from 'react';

import { AclStateExplanation } from './AclStateExplanation';
import { AclRedactedCell } from './AclRedactedCell';
import { ACL_EXPLANATION_COPY } from './copy';
import {
  createSafeChartPoint,
  createSafeExportValue,
  createSafeSortValue,
  isSecretLikeKey,
  sanitizeAclStateForClientCache,
  summarizeCoverage,
} from './redaction';
import { PositionsAclSidecar } from './types';

export interface PositionsAclDashboardMetric {
  key: string;
  label: string;
  value: unknown;
}

interface PositionsAclDashboardExposureProps {
  sidecar: PositionsAclSidecar;
  metrics: PositionsAclDashboardMetric[];
  state?: 'applied' | 'unavailable' | 'scope_changed' | 'awaiting_resnapshot';
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: 'grid',
    gap: 10,
    padding: 12,
    border: '1px solid #334155',
    borderRadius: 8,
    background: '#0f172a',
    color: '#e5e7eb',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: 14,
  },
  badge: {
    display: 'inline-flex',
    width: 'fit-content',
    border: '1px solid #64748b',
    borderRadius: 6,
    padding: '3px 8px',
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: 700,
  },
  warningBadge: {
    borderColor: '#f59e0b',
    color: '#fde68a',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 8,
  },
  metric: {
    display: 'grid',
    gap: 4,
  },
  label: {
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
};

const STATE_COPY: Record<NonNullable<PositionsAclDashboardExposureProps['state']>, string> = {
  applied: 'ACL-filtered view',
  unavailable: ACL_EXPLANATION_COPY.unavailable.label,
  scope_changed: ACL_EXPLANATION_COPY.scope_changed.label,
  awaiting_resnapshot: ACL_EXPLANATION_COPY.awaiting_resnapshot.label,
};

function safeMetricOutputs(metrics: PositionsAclDashboardMetric[]) {
  return metrics.reduce<{
    chartValues: Record<string, number | null>;
    sortValues: Record<string, string | number | null>;
    exportValues: Record<string, string | number | null>;
  }>(
    (safe, metric) => {
      if (isSecretLikeKey(metric.key)) {
        return safe;
      }
      safe.chartValues[metric.key] = createSafeChartPoint(metric.value);
      safe.sortValues[metric.key] = createSafeSortValue(metric.value);
      safe.exportValues[metric.key] = createSafeExportValue(metric.value);
      return safe;
    },
    { chartValues: {}, sortValues: {}, exportValues: {} },
  );
}

export function PositionsAclDashboardExposure({
  sidecar,
  metrics,
  state = 'applied',
}: PositionsAclDashboardExposureProps) {
  const coverage = summarizeCoverage(sidecar);
  const renderableMetrics = metrics.filter((metric) => !isSecretLikeKey(metric.key));
  const safeOutputs = safeMetricOutputs(renderableMetrics);
  const explanationKind = state === 'applied' ? null : state;
  const safeClientState = sanitizeAclStateForClientCache({
    sidecar,
    metrics: renderableMetrics.map((metric) => ({
      key: metric.key,
      label: metric.label,
      value: metric.value,
    })),
    safeOutputs,
    state,
  });

  return (
    <section
      aria-label="Positions ACL dashboard exposure"
      data-acl-dashboard-exposure="enabled"
      data-acl-dashboard-state={state}
      data-acl-cache-state={JSON.stringify(safeClientState)}
      data-acl-chart-values={JSON.stringify(safeOutputs.chartValues)}
      data-acl-sort-values={JSON.stringify(safeOutputs.sortValues)}
      data-acl-export-values={JSON.stringify(safeOutputs.exportValues)}
      style={styles.panel}
    >
      <div style={styles.header}>
        <h3 style={styles.title}>Positions ACL View</h3>
        <span
          aria-label={STATE_COPY[state]}
          data-acl-state-copy={state}
          style={{
            ...styles.badge,
            ...(state === 'applied' ? {} : styles.warningBadge),
          }}
        >
          {STATE_COPY[state]}
        </span>
        {explanationKind && (
          <AclStateExplanation
            kind={explanationKind}
            id={`acl-dashboard-${explanationKind}`}
          />
        )}
        {coverage && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-label={coverage} data-acl-coverage="partial" style={styles.badge}>
              {coverage}
            </span>
            <AclStateExplanation kind="partial" id="acl-dashboard-partial" />
          </span>
        )}
      </div>
      <div style={styles.grid}>
        {renderableMetrics.map((metric) => (
          <div key={metric.key} style={styles.metric}>
            <span style={styles.label}>{metric.label}</span>
            <AclRedactedCell label={metric.label} value={metric.value} />
          </div>
        ))}
      </div>
    </section>
  );
}
